import type { IRootState } from "../app/store";
import { createAppSlice } from "../app/createAppSlice";
import { createSelector } from "@reduxjs/toolkit";
import type { IDrofusInterfaceDiscipline } from "@multiconsult-group/multiplacer-core";
import type {
  GetMultiplacerDrofusInterfaceRunsApiArg,
  GetMultiplacerDrofusInterfaceRunsApiResponse,
  PostMultiplacerDrofusInterfaceArticlesApiArg,
  PostMultiplacerDrofusInterfaceOccurrencesApiArg,
} from "../../openapi";
import { openapi } from "../../openapi";

type DrofusInterfaceRun = GetMultiplacerDrofusInterfaceRunsApiResponse["items"][number];

const EMPTY_DROFUS_INTERFACE_RUNS: DrofusInterfaceRun[] = [];

export interface IDrofusForekomsterNeedsFilter {
  electrical?: boolean;
  ikt?: boolean;
  automation?: boolean;
  vvs?: boolean;
}

export interface IDrofusForekomsterSort {
  field:
    | "occurrenceId"
    | "roomFunctionNo"
    | "roomArchitectNo"
    | "roomName"
    | "compCode"
    | "articleName"
    | "quantity"
    | "capturedAt";
  direction: "asc" | "desc";
}

export interface IDrofusForekomsterArticleSort {
  field:
    | "articleId"
    | "compCode"
    | "articleName"
    | "occurrenceCount"
    | "uniqueRoomCount"
    | "totalQuantity";
  direction: "asc" | "desc";
}

export interface IDrofusForekomsterState {
  selectedRunId: string | null;
  activeView: "occurrences" | "articles";
  tableViewMode: "simple" | "full";
  filtersOpen: boolean;
  search: string;
  limit: number;
  offset: number;
  articleLimit: number;
  articleOffset: number;
  interfaceRelevant?: boolean;
  toBeDrawn?: boolean;
  affectedDisciplines: IDrofusInterfaceDiscipline[];
  needs: IDrofusForekomsterNeedsFilter;
  sort: IDrofusForekomsterSort;
  articleSort: IDrofusForekomsterArticleSort;
  roomViewer: {
    projectNo: number | null;
    roomFunctionNo: string | null;
    articleCompCode: string | null;
    articleToBeDrawn: boolean | null;
    showLengthComponents: boolean;
  };
}

const initialState: IDrofusForekomsterState = {
  selectedRunId: null,
  activeView: "occurrences",
  tableViewMode: "simple",
  filtersOpen: true,
  search: "",
  limit: 100,
  offset: 0,
  articleLimit: 100,
  articleOffset: 0,
  interfaceRelevant: undefined,
  toBeDrawn: undefined,
  affectedDisciplines: [],
  needs: {},
  sort: {
    field: "roomFunctionNo",
    direction: "asc",
  },
  articleSort: {
    field: "compCode",
    direction: "asc",
  },
  roomViewer: {
    projectNo: null,
    roomFunctionNo: null,
    articleCompCode: null,
    articleToBeDrawn: null,
    showLengthComponents: true,
  },
};

export const drofusForekomsterSlice = createAppSlice({
  name: "drofusForekomster",
  initialState,
  reducers: (create) => ({
    setDrofusForekomsterSelectedRunId: create.reducer<string | null>((state, action) => {
      state.selectedRunId = action.payload;
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterActiveView: create.reducer<"occurrences" | "articles">((state, action) => {
      state.activeView = action.payload;
    }),
    setDrofusForekomsterArticleLimit: create.reducer<number>((state, action) => {
      state.articleLimit = action.payload;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterArticleOffset: create.reducer<number>((state, action) => {
      state.articleOffset = action.payload;
    }),
    setDrofusForekomsterTableViewMode: create.reducer<"simple" | "full">((state, action) => {
      state.tableViewMode = action.payload;
    }),
    setDrofusForekomsterFiltersOpen: create.reducer<boolean>((state, action) => {
      state.filtersOpen = action.payload;
    }),
    toggleDrofusForekomsterFiltersOpen: create.reducer((state) => {
      state.filtersOpen = !state.filtersOpen;
    }),
    setDrofusForekomsterSearch: create.reducer<string>((state, action) => {
      state.search = action.payload;
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterLimit: create.reducer<number>((state, action) => {
      state.limit = action.payload;
      state.offset = 0;
    }),
    setDrofusForekomsterOffset: create.reducer<number>((state, action) => {
      state.offset = action.payload;
    }),
    setDrofusForekomsterInterfaceRelevant: create.reducer<boolean | undefined>((state, action) => {
      state.interfaceRelevant = action.payload;
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterToBeDrawn: create.reducer<boolean | undefined>((state, action) => {
      state.toBeDrawn = action.payload;
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterAffectedDisciplines: create.reducer<IDrofusInterfaceDiscipline[]>(
      (state, action) => {
        state.affectedDisciplines = Array.from(new Set(action.payload));
        state.offset = 0;
        state.articleOffset = 0;
      }
    ),
    toggleDrofusForekomsterAffectedDiscipline: create.reducer<IDrofusInterfaceDiscipline>(
      (state, action) => {
        const discipline = action.payload;
        state.affectedDisciplines = state.affectedDisciplines.includes(discipline)
          ? state.affectedDisciplines.filter((item) => item !== discipline)
          : [...state.affectedDisciplines, discipline];
        state.offset = 0;
        state.articleOffset = 0;
      }
    ),
    setDrofusForekomsterNeeds: create.reducer<IDrofusForekomsterNeedsFilter>((state, action) => {
      state.needs = action.payload;
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterNeed: create.reducer<{
      key: keyof IDrofusForekomsterNeedsFilter;
      value: boolean | undefined;
    }>((state, action) => {
      const { key, value } = action.payload;
      if (typeof value === "undefined") {
        delete state.needs[key];
      } else {
        state.needs[key] = value;
      }
      state.offset = 0;
      state.articleOffset = 0;
    }),
    setDrofusForekomsterSort: create.reducer<IDrofusForekomsterSort>((state, action) => {
      state.sort = action.payload;
      state.offset = 0;
    }),
    setDrofusForekomsterArticleSort: create.reducer<IDrofusForekomsterArticleSort>(
      (state, action) => {
        state.articleSort = action.payload;
        state.articleOffset = 0;
      }
    ),
    resetDrofusForekomsterFilters: create.reducer((state) => {
      state.search = "";
      state.offset = 0;
      state.articleOffset = 0;
      state.interfaceRelevant = undefined;
      state.toBeDrawn = undefined;
      state.affectedDisciplines = [];
      state.needs = {};
      state.sort = initialState.sort;
      state.articleSort = initialState.articleSort;
    }),
    setDrofusOccurrenceRoomViewerContext: create.reducer<{
      projectNo: number | null;
      roomFunctionNo?: string | null;
      articleCompCode?: string | null;
      articleToBeDrawn?: boolean | null;
    }>((state, action) => {
      state.roomViewer.projectNo = action.payload.projectNo;
      state.roomViewer.roomFunctionNo = action.payload.roomFunctionNo ?? null;
      state.roomViewer.articleCompCode = action.payload.articleCompCode ?? null;
      state.roomViewer.articleToBeDrawn = action.payload.articleToBeDrawn ?? null;
    }),
    setDrofusOccurrenceRoomViewerShowLengthComponents: create.reducer<boolean>((state, action) => {
      state.roomViewer.showLengthComponents = action.payload;
    }),
  }),
});

export const {
  setDrofusForekomsterSelectedRunId,
  setDrofusForekomsterActiveView,
  setDrofusForekomsterTableViewMode,
  setDrofusForekomsterArticleLimit,
  setDrofusForekomsterArticleOffset,
  setDrofusForekomsterFiltersOpen,
  toggleDrofusForekomsterFiltersOpen,
  setDrofusForekomsterSearch,
  setDrofusForekomsterLimit,
  setDrofusForekomsterOffset,
  setDrofusForekomsterInterfaceRelevant,
  setDrofusForekomsterToBeDrawn,
  setDrofusForekomsterAffectedDisciplines,
  toggleDrofusForekomsterAffectedDiscipline,
  setDrofusForekomsterNeeds,
  setDrofusForekomsterNeed,
  setDrofusForekomsterSort,
  setDrofusForekomsterArticleSort,
  resetDrofusForekomsterFilters,
  setDrofusOccurrenceRoomViewerContext,
  setDrofusOccurrenceRoomViewerShowLengthComponents,
} = drofusForekomsterSlice.actions;

export const selectDrofusForekomsterState = (state: IRootState) => state.drofusForekomster;
export const selectDrofusForekomsterSelectedRunId = (state: IRootState) =>
  state.drofusForekomster.selectedRunId;
export const selectDrofusForekomsterActiveView = (state: IRootState) =>
  state.drofusForekomster.activeView;
export const selectDrofusForekomsterTableViewMode = (state: IRootState) =>
  state.drofusForekomster.tableViewMode;
export const selectDrofusForekomsterFiltersOpen = (state: IRootState) =>
  state.drofusForekomster.filtersOpen;
export const selectDrofusForekomsterSearch = (state: IRootState) => state.drofusForekomster.search;
export const selectDrofusForekomsterLimit = (state: IRootState) => state.drofusForekomster.limit;
export const selectDrofusForekomsterOffset = (state: IRootState) => state.drofusForekomster.offset;
export const selectDrofusForekomsterArticleLimit = (state: IRootState) =>
  state.drofusForekomster.articleLimit;
export const selectDrofusForekomsterArticleOffset = (state: IRootState) =>
  state.drofusForekomster.articleOffset;
export const selectDrofusForekomsterInterfaceRelevant = (state: IRootState) =>
  state.drofusForekomster.interfaceRelevant;
export const selectDrofusForekomsterToBeDrawn = (state: IRootState) =>
  state.drofusForekomster.toBeDrawn;
export const selectDrofusForekomsterAffectedDisciplines = (state: IRootState) =>
  state.drofusForekomster.affectedDisciplines;
export const selectDrofusForekomsterNeeds = (state: IRootState) => state.drofusForekomster.needs;
export const selectDrofusForekomsterSort = (state: IRootState) => state.drofusForekomster.sort;
export const selectDrofusForekomsterArticleSort = (state: IRootState) =>
  state.drofusForekomster.articleSort;
export const selectDrofusOccurrenceRoomViewerState = (state: IRootState) =>
  state.drofusForekomster.roomViewer;

export const selectDrofusForekomsterRunsApiArg = createSelector(
  (state: IRootState) => state.design.projectNo,
  (projectNo): GetMultiplacerDrofusInterfaceRunsApiArg | null => {
    if (!Number.isFinite(projectNo) || projectNo <= 0) return null;

    return {
      projectNo,
      limit: 100,
      offset: 0,
    };
  }
);

export const selectDrofusForekomsterRuns = (state: IRootState): DrofusInterfaceRun[] => {
  const args = selectDrofusForekomsterRunsApiArg(state);
  if (!args) return EMPTY_DROFUS_INTERFACE_RUNS;

  return (
    openapi.endpoints.getMultiplacerDrofusInterfaceRuns.select(args)(state).data?.items ??
    EMPTY_DROFUS_INTERFACE_RUNS
  );
};

export const selectDrofusForekomsterLatestRunId = createSelector(
  selectDrofusForekomsterRuns,
  (runs) => {
    let latestRun: (DrofusInterfaceRun & { _id: string }) | null = null;

    for (const run of runs) {
      if (typeof run._id !== "string") continue;
      if (!latestRun) {
        latestRun = run as DrofusInterfaceRun & { _id: string };
        continue;
      }

      const runStartedAt = run.startedAt ? new Date(run.startedAt).getTime() : 0;
      const latestStartedAt = latestRun.startedAt ? new Date(latestRun.startedAt).getTime() : 0;

      if (
        runStartedAt > latestStartedAt ||
        (runStartedAt === latestStartedAt && run._id.localeCompare(latestRun._id) > 0)
      ) {
        latestRun = run as DrofusInterfaceRun & { _id: string };
      }
    }

    return latestRun?._id ?? null;
  }
);

export const selectDrofusForekomsterSelectedRun = createSelector(
  selectDrofusForekomsterRuns,
  selectDrofusForekomsterSelectedRunId,
  (runs, selectedRunId) => runs.find((run) => run._id === selectedRunId) ?? null
);

export const selectDrofusForekomsterSelectedRunExists = createSelector(
  selectDrofusForekomsterSelectedRun,
  (selectedRun) => selectedRun != null
);

export const selectDrofusForekomsterRunningRun = createSelector(
  selectDrofusForekomsterRuns,
  (runs) =>
    runs
      .filter((run): run is DrofusInterfaceRun & { _id: string } => typeof run._id === "string")
      .find((run) => run.status === "queued" || run.status === "running") ?? null
);

const selectDrofusForekomsterBaseApiBody = createSelector(
  selectDrofusForekomsterState,
  (state: IRootState) => state.design.projectNo,
  (state: IRootState) => state.pnsSelection.selectedIds,
  (drofusForekomster, projectNo, selectedPnsIds) => {
    if (!drofusForekomster.selectedRunId || !Number.isFinite(projectNo) || projectNo <= 0) {
      return null;
    }

    const search = drofusForekomster.search.trim();
    const pns = Array.from(new Set(selectedPnsIds.map((id) => id.trim()).filter(Boolean)));
    const hasNeedsFilter = Object.values(drofusForekomster.needs).some(
      (value) => typeof value === "boolean"
    );

    return {
      projectNo,
      runId: drofusForekomster.selectedRunId,
      ...(search.length > 0 ? { search } : {}),
      ...(pns.length > 0 ? { pns } : {}),
      ...(typeof drofusForekomster.interfaceRelevant === "boolean"
        ? { interfaceRelevant: drofusForekomster.interfaceRelevant }
        : {}),
      ...(typeof drofusForekomster.toBeDrawn === "boolean"
        ? { toBeDrawn: drofusForekomster.toBeDrawn }
        : {}),
      ...(drofusForekomster.affectedDisciplines.length > 0
        ? { affectedDisciplines: drofusForekomster.affectedDisciplines }
        : {}),
      ...(hasNeedsFilter ? { needs: drofusForekomster.needs } : {}),
    };
  }
);

export const selectDrofusForekomsterOccurrencesApiArg = createSelector(
  selectDrofusForekomsterState,
  selectDrofusForekomsterBaseApiBody,
  (drofusForekomster, baseBody): PostMultiplacerDrofusInterfaceOccurrencesApiArg | null => {
    if (!baseBody) return null;

    return {
      body: {
        ...baseBody,
        limit: drofusForekomster.limit,
        offset: drofusForekomster.offset,
        sort: drofusForekomster.sort,
      },
    };
  }
);

export const selectDrofusForekomsterArticlesApiArg = createSelector(
  selectDrofusForekomsterState,
  selectDrofusForekomsterBaseApiBody,
  (drofusForekomster, baseBody): PostMultiplacerDrofusInterfaceArticlesApiArg | null => {
    if (!baseBody) return null;

    return {
      body: {
        ...baseBody,
        limit: drofusForekomster.articleLimit,
        offset: drofusForekomster.articleOffset,
        sort: drofusForekomster.articleSort,
      },
    };
  }
);
